import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { bankApi, formatMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowRight, Vault, Wallet, PiggyBank, Globe2, MapPin } from "lucide-react";

const cards = [
  {
    key: "escrow",
    label: "Escrow Account",
    subtitle: "Institutional Custody",
    icon: Vault,
    span: "lg:col-span-2",
    accent: true,
  },
  {
    key: "checking",
    label: "Checking",
    subtitle: "Operating Reserve",
    icon: Wallet,
    span: "lg:col-span-1",
  },
  {
    key: "savings",
    label: "Savings",
    subtitle: "Term Deposit",
    icon: PiggyBank,
    span: "lg:col-span-1",
  },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [balances, setBalances] = useState(null);
  const [txs, setTxs] = useState([]);

  useEffect(() => {
    bankApi.accounts().then(setBalances).catch(() => {});
    bankApi.transactions().then((t) => setTxs(t.slice(0, 5))).catch(() => {});
  }, []);

  const total =
    balances &&
    (balances.escrow + balances.checking + balances.savings);

  return (
    <div className="space-y-12">
      {/* Header */}
      <section>
        <div className="text-institutional-gold uppercase tracking-[0.25em] text-xs font-semibold mb-3">
          Portfolio Overview
        </div>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-federal-navy tracking-tight">
              Good day, {user?.full_name?.split(" ")[0] || user?.username}.
            </h1>
            <p className="text-slate-600 mt-3 max-w-xl">
              Your consolidated custodial position with the Federal Reserve
              Bank of Custody as of{" "}
              <span className="text-federal-navy">
                {new Date().toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
              .
            </p>
          </div>
          <div className="border-l-2 border-institutional-gold pl-6">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">
              Total Assets Under Custody
            </div>
            <div
              className="font-serif text-4xl lg:text-5xl text-federal-navy tabular-nums"
              data-testid="total-balance"
            >
              {total !== null && total !== undefined ? formatMoney(total) : "—"}
            </div>
            <div className="text-xs text-slate-500 mt-1 font-mono">
              Acct № {user?.account_number}
            </div>
          </div>
        </div>
      </section>

      <div className="gold-divider" />

      {/* Account cards */}
      <section>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {cards.map((c) => {
            const Icon = c.icon;
            const value = balances ? balances[c.key] : null;
            const isEscrow = c.accent;
            return (
              <div
                key={c.key}
                data-testid={`account-card-${c.key}`}
                className={`${c.span} border ${
                  isEscrow
                    ? "border-federal-navy bg-federal-navy text-white grain-overlay"
                    : "border-slate-200 bg-white"
                } p-8 relative flex flex-col justify-between min-h-[240px]`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div
                      className={`uppercase tracking-[0.2em] text-[10px] font-semibold mb-2 ${
                        isEscrow ? "text-institutional-gold" : "text-institutional-gold"
                      }`}
                    >
                      {c.subtitle}
                    </div>
                    <div
                      className={`font-serif text-2xl ${
                        isEscrow ? "text-white" : "text-federal-navy"
                      }`}
                    >
                      {c.label}
                    </div>
                  </div>
                  <Icon
                    className={`w-6 h-6 ${
                      isEscrow ? "text-institutional-gold" : "text-slate-400"
                    }`}
                    strokeWidth={1.5}
                  />
                </div>
                <div>
                  <div
                    className={`font-serif tabular-nums tracking-tight ${
                      isEscrow
                        ? "text-white text-5xl lg:text-6xl"
                        : "text-federal-navy text-4xl"
                    }`}
                    data-testid={`balance-${c.key}`}
                  >
                    {value !== null && value !== undefined ? formatMoney(value) : "—"}
                  </div>
                  <div
                    className={`mt-3 text-xs uppercase tracking-widest ${
                      isEscrow ? "text-white/50" : "text-slate-400"
                    }`}
                  >
                    Available Balance
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Quick actions */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link
          to="/transfer?type=domestic"
          data-testid="quick-domestic-transfer"
          className="group border border-slate-200 bg-white p-8 hover:border-federal-navy transition-colors flex items-center justify-between"
        >
          <div className="flex items-center gap-5">
            <div className="w-12 h-12 border border-slate-300 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-federal-navy" strokeWidth={1.5} />
            </div>
            <div>
              <div className="uppercase tracking-widest text-[10px] text-institutional-gold font-semibold">
                Wire
              </div>
              <div className="font-serif text-2xl text-federal-navy">
                Domestic Transfer
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Limit per transfer: {formatMoney(10_000_000)}
              </div>
            </div>
          </div>
          <ArrowRight
            className="w-5 h-5 text-slate-400 group-hover:text-institutional-gold transition-colors"
            strokeWidth={1.5}
          />
        </Link>
        <Link
          to="/transfer?type=international"
          data-testid="quick-international-transfer"
          className="group border border-slate-200 bg-white p-8 hover:border-federal-navy transition-colors flex items-center justify-between"
        >
          <div className="flex items-center gap-5">
            <div className="w-12 h-12 border border-slate-300 flex items-center justify-center">
              <Globe2 className="w-5 h-5 text-federal-navy" strokeWidth={1.5} />
            </div>
            <div>
              <div className="uppercase tracking-widest text-[10px] text-institutional-gold font-semibold">
                SWIFT
              </div>
              <div className="font-serif text-2xl text-federal-navy">
                International Transfer
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Limit per transfer: {formatMoney(1_500_000)}
              </div>
            </div>
          </div>
          <ArrowRight
            className="w-5 h-5 text-slate-400 group-hover:text-institutional-gold transition-colors"
            strokeWidth={1.5}
          />
        </Link>
      </section>

      {/* Recent activity */}
      <section>
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-institutional-gold uppercase tracking-[0.25em] text-xs font-semibold mb-2">
              Recent Ledger
            </div>
            <h2 className="font-serif text-3xl text-federal-navy">Latest activity</h2>
          </div>
          <Link
            to="/transactions"
            data-testid="view-all-txs"
            className="text-sm uppercase tracking-widest text-federal-navy hover:text-institutional-gold transition-colors"
          >
            View ledger →
          </Link>
        </div>

        <div className="border border-slate-200 bg-white">
          {txs.length === 0 ? (
            <div className="p-12 text-center text-slate-500 italic">
              No transactions on the ledger yet.
            </div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-slate-200">
                <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="text-left px-6 py-4 font-medium">Date</th>
                  <th className="text-left px-6 py-4 font-medium">Type</th>
                  <th className="text-left px-6 py-4 font-medium">Recipient</th>
                  <th className="text-left px-6 py-4 font-medium">Status</th>
                  <th className="text-right px-6 py-4 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {txs.map((t) => (
                  <tr key={t.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-6 py-4 text-sm text-slate-600 font-mono">
                      {new Date(t.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className="uppercase tracking-widest text-[10px] text-institutional-gold font-semibold">
                        {t.transfer_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-federal-navy">
                      {t.recipient_name}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {t.status === "on_hold_fdic" ? (
                        <span className="uppercase tracking-widest text-[10px] font-semibold text-[#800000]">
                          FDIC Hold
                        </span>
                      ) : (
                        <span className="uppercase tracking-widest text-[10px] font-semibold text-[#1E5631]">
                          Completed
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-right tabular-nums font-medium text-[#800000]">
                      − {formatMoney(t.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
